import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { 
  Plus, Edit2, Trash2, Receipt, Car, Plane, Utensils, Hotel, 
  DollarSign, CheckCircle, XCircle, Clock, Send, Upload,
  FolderOpen, MapPin, Calendar, CreditCard, Wallet, Building2,
  Paperclip, Image, FileText, ExternalLink
} from "lucide-react";
import api, { BACKEND_URL } from "@/lib/api";

const EXPENSE_CATEGORIES = [
  { id: "travel", name: "Travel", icon: Plane, description: "Airfare, train, rideshare" },
  { id: "meals", name: "Meals & Entertainment", icon: Utensils, description: "Business meals" },
  { id: "lodging", name: "Lodging", icon: Hotel, description: "Hotel, accommodation" },
  { id: "vehicle_fuel", name: "Vehicle - Fuel", icon: Car, description: "Gas, diesel" },
  { id: "vehicle_parking", name: "Vehicle - Parking & Tolls", icon: MapPin, description: "Parking, tolls" },
  { id: "vehicle_mileage", name: "Vehicle - Mileage", icon: Car, description: "Mileage reimbursement" },
  { id: "vehicle_rental", name: "Vehicle - Rental", icon: Car, description: "Car rental" },
  { id: "office_supplies", name: "Office Supplies", icon: Building2, description: "Supplies" },
  { id: "communication", name: "Communication", icon: Building2, description: "Phone, internet" },
  { id: "other", name: "Other Business Expense", icon: Receipt, description: "Miscellaneous" },
];

const PAYMENT_METHODS = [
  { id: "company_card", name: "Company Card", icon: CreditCard },
  { id: "personal_card", name: "Personal Card (Reimbursable)", icon: CreditCard },
  { id: "personal_cash", name: "Personal Cash (Reimbursable)", icon: Wallet },
];

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-gray-500/20 text-gray-400", icon: Edit2 },
  submitted: { label: "Pending Approval", color: "bg-yellow-500/20 text-yellow-400", icon: Clock },
  approved: { label: "Approved", color: "bg-blue-500/20 text-blue-400", icon: CheckCircle },
  rejected: { label: "Rejected", color: "bg-red-500/20 text-red-400", icon: XCircle },
  reimbursed: { label: "Reimbursed", color: "bg-green-500/20 text-green-400", icon: DollarSign },
};

const IRS_MILEAGE_RATE = 0.67;

export default function ExpensesPage({ companyId, company }) {
  const [activeTab, setActiveTab] = useState("expenses");
  const [expenses, setExpenses] = useState([]);
  const [trips, setTrips] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterTrip, setFilterTrip] = useState("");

  // Expense Dialog State
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split("T")[0]);
  const [expenseCategory, setExpenseCategory] = useState("");
  const [expenseAmount, setExpenseAmount] = useState("");
  const [expenseMerchant, setExpenseMerchant] = useState("");
  const [expenseDescription, setExpenseDescription] = useState("");
  const [expensePaymentMethod, setExpensePaymentMethod] = useState("personal_card");
  const [expenseTripId, setExpenseTripId] = useState("");
  const [expenseMileage, setExpenseMileage] = useState("");
  const [expenseReceiptFile, setExpenseReceiptFile] = useState(null);
  const [saving, setSaving] = useState(false);

  // Trip Dialog State
  const [showTripDialog, setShowTripDialog] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  const [tripName, setTripName] = useState("");
  const [tripDescription, setTripDescription] = useState("");
  const [tripStartDate, setTripStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [tripEndDate, setTripEndDate] = useState("");
  const [tripDestination, setTripDestination] = useState("");
  const [tripPurpose, setTripPurpose] = useState("");

  // Rejection Dialog
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectingExpense, setRejectingExpense] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  // Receipt Upload Dialog
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [uploadingExpense, setUploadingExpense] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);

  // Receipt Preview Dialog
  const [showReceiptPreview, setShowReceiptPreview] = useState(false);
  const [previewReceipt, setPreviewReceipt] = useState(null);

  const loadData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append("status", filterStatus);
      if (filterCategory) params.append("category", filterCategory);
      if (filterTrip) params.append("trip_id", filterTrip);

      const [expensesRes, tripsRes] = await Promise.all([
        api.get(`/companies/${companyId}/expenses?${params.toString()}`),
        api.get(`/companies/${companyId}/trips`)
      ]);

      setExpenses(expensesRes.data.expenses || []);
      setStats(expensesRes.data.stats || {});
      setTrips(tripsRes.data.trips || []);
    } catch (err) {
      toast.error("Failed to load expenses");
    } finally {
      setLoading(false);
    }
  }, [companyId, filterStatus, filterCategory, filterTrip]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset expense form
  const resetExpenseForm = () => {
    setExpenseDate(new Date().toISOString().split("T")[0]);
    setExpenseCategory("");
    setExpenseAmount("");
    setExpenseMerchant("");
    setExpenseDescription("");
    setExpensePaymentMethod("personal_card");
    setExpenseTripId("");
    setExpenseMileage("");
    setExpenseReceiptFile(null);
    setEditingExpense(null);
  };

  // Reset trip form
  const resetTripForm = () => {
    setTripName("");
    setTripDescription("");
    setTripStartDate(new Date().toISOString().split("T")[0]);
    setTripEndDate("");
    setTripDestination("");
    setTripPurpose("");
    setEditingTrip(null);
  };

  // Open expense dialog for new expense
  const openNewExpense = () => {
    resetExpenseForm();
    setShowExpenseDialog(true);
  };

  // Open expense dialog for editing
  const openEditExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseDate(expense.date);
    setExpenseCategory(expense.category);
    setExpenseAmount(String(expense.amount));
    setExpenseMerchant(expense.merchant || "");
    setExpenseDescription(expense.description || "");
    setExpensePaymentMethod(expense.payment_method);
    setExpenseTripId(expense.trip_id || "");
    setExpenseMileage(expense.mileage ? String(expense.mileage) : "");
    setExpenseReceiptFile(null);
    setShowExpenseDialog(true);
  };

  // Validate + set receipt file chosen inside the expense dialog
  const handleDialogReceiptSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload JPEG, PNG, WebP, or PDF");
      e.target.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB");
      e.target.value = "";
      return;
    }
    setExpenseReceiptFile(file);
  };

  // Save expense
  const handleSaveExpense = async () => {
    if (!expenseCategory) {
      toast.error("Please select a category");
      return;
    }
    if (!expenseAmount && expenseCategory !== "vehicle_mileage") {
      toast.error("Please enter an amount");
      return;
    }
    if (expenseCategory === "vehicle_mileage" && !expenseMileage) {
      toast.error("Please enter mileage");
      return;
    }

    setSaving(true);
    try {
      const data = {
        date: expenseDate,
        category: expenseCategory,
        amount: parseFloat(expenseAmount) || 0,
        merchant: expenseMerchant.trim(),
        description: expenseDescription.trim(),
        payment_method: expensePaymentMethod,
        trip_id: expenseTripId || null,
        mileage: expenseMileage ? parseFloat(expenseMileage) : null,
      };

      // For mileage, calculate amount automatically
      if (expenseCategory === "vehicle_mileage" && expenseMileage) {
        data.amount = parseFloat(expenseMileage) * IRS_MILEAGE_RATE;
      }

      let expenseId;
      if (editingExpense) {
        await api.put(`/companies/${companyId}/expenses/${editingExpense.expense_id}`, data);
        expenseId = editingExpense.expense_id;
        toast.success("Expense updated");
      } else {
        const res = await api.post(`/companies/${companyId}/expenses`, data);
        expenseId = res.data.expense_id;
        toast.success("Expense created");
      }

      if (expenseReceiptFile && expenseId) {
        try {
          const formData = new FormData();
          formData.append('file', expenseReceiptFile);
          await api.post(
            `/companies/${companyId}/expenses/${expenseId}/receipt`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
          toast.success("Receipt attached");
        } catch (err) {
          toast.error("Expense saved, but receipt upload failed. You can attach it from the list.");
        }
      }

      setShowExpenseDialog(false);
      resetExpenseForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save expense");
    } finally {
      setSaving(false);
    }
  };

  // Delete expense
  const handleDeleteExpense = async (expense) => {
    if (!["draft", "rejected"].includes(expense.status)) {
      toast.error("Only draft or rejected expenses can be deleted");
      return;
    }
    if (!confirm(`Delete this expense for $${expense.amount.toFixed(2)}?`)) return;

    try {
      await api.delete(`/companies/${companyId}/expenses/${expense.expense_id}`);
      toast.success("Expense deleted");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete expense");
    }
  };

  // Submit expense for approval
  const handleSubmitExpense = async (expense) => {
    try {
      await api.post(`/companies/${companyId}/expenses/${expense.expense_id}/submit`);
      toast.success("Expense submitted for approval");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit expense");
    }
  };

  // Approve expense
  const handleApproveExpense = async (expense) => {
    try {
      await api.post(`/companies/${companyId}/expenses/${expense.expense_id}/approve`);
      toast.success("Expense approved");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve expense");
    }
  };

  // Open reject dialog
  const openRejectDialog = (expense) => {
    setRejectingExpense(expense);
    setRejectionReason("");
    setShowRejectDialog(true);
  };

  // Reject expense
  const handleRejectExpense = async () => {
    if (!rejectionReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }

    try {
      await api.post(`/companies/${companyId}/expenses/${rejectingExpense.expense_id}/reject`, {
        status: "rejected",
        rejection_reason: rejectionReason.trim()
      });
      toast.success("Expense rejected");
      setShowRejectDialog(false);
      setRejectingExpense(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject expense");
    }
  };

  // Mark as reimbursed
  const handleReimburse = async (expense) => {
    try {
      await api.post(`/companies/${companyId}/expenses/${expense.expense_id}/reimburse`);
      toast.success("Expense marked as reimbursed");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to mark as reimbursed");
    }
  };

  // One-click bulk action on all eligible expenses of a trip
  const handleTripBulkAction = async (trip, action) => {
    try {
      const res = await api.post(`/companies/${companyId}/trips/${trip.trip_id}/bulk-action`, { action });
      toast.success(res.data.message);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk action failed");
    }
  };

  // Download trip expense report PDF
  const handleTripReport = async (trip) => {
    try {
      toast.info("Generating expense report...");
      const res = await api.get(`/companies/${companyId}/trips/${trip.trip_id}/report`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Expense_Report_${trip.name.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Expense report downloaded");
    } catch (err) {
      toast.error("Failed to generate report");
    }
  };

  // Open receipt upload dialog
  const openReceiptUpload = (expense) => {
    setUploadingExpense(expense);
    setShowReceiptDialog(true);
  };

  // Handle receipt upload
  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload JPEG, PNG, WebP, or PDF");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB");
      return;
    }

    setUploadingReceipt(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await api.post(
        `/companies/${companyId}/expenses/${uploadingExpense.expense_id}/receipt`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      toast.success("Receipt uploaded successfully");
      setShowReceiptDialog(false);
      setUploadingExpense(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload receipt");
    } finally {
      setUploadingReceipt(false);
    }
  };

  // View receipt
  const viewReceipt = (expense) => {
    if (expense.receipt_url) {
      setPreviewReceipt({
        url: `${BACKEND_URL}/api${expense.receipt_url}`,
        expense: expense
      });
      setShowReceiptPreview(true);
    }
  };

  // Open trip dialog for new trip
  const openNewTrip = () => {
    resetTripForm();
    setShowTripDialog(true);
  };

  // Open trip dialog for editing
  const openEditTrip = (trip) => {
    setEditingTrip(trip);
    setTripName(trip.name);
    setTripDescription(trip.description || "");
    setTripStartDate(trip.start_date);
    setTripEndDate(trip.end_date || "");
    setTripDestination(trip.destination || "");
    setTripPurpose(trip.purpose || "");
    setShowTripDialog(true);
  };

  // Save trip
  const handleSaveTrip = async () => {
    if (!tripName.trim()) {
      toast.error("Please enter a trip name");
      return;
    }

    setSaving(true);
    try {
      const data = {
        name: tripName.trim(),
        description: tripDescription.trim(),
        start_date: tripStartDate,
        end_date: tripEndDate || null,
        destination: tripDestination.trim(),
        purpose: tripPurpose.trim(),
      };

      if (editingTrip) {
        await api.put(`/companies/${companyId}/trips/${editingTrip.trip_id}`, data);
        toast.success("Trip updated");
      } else {
        await api.post(`/companies/${companyId}/trips`, data);
        toast.success("Trip created");
      }

      setShowTripDialog(false);
      resetTripForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save trip");
    } finally {
      setSaving(false);
    }
  };

  // Delete trip
  const handleDeleteTrip = async (trip) => {
    if (!confirm(`Delete trip "${trip.name}"? Expenses will be unlinked but not deleted.`)) return;

    try {
      await api.delete(`/companies/${companyId}/trips/${trip.trip_id}`);
      toast.success("Trip deleted");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete trip");
    }
  };

  const getCategoryIcon = (categoryId) => {
    const cat = EXPENSE_CATEGORIES.find(c => c.id === categoryId);
    return cat ? cat.icon : Receipt;
  };

  const getCategoryName = (categoryId) => {
    const cat = EXPENSE_CATEGORIES.find(c => c.id === categoryId);
    return cat ? cat.name : categoryId;
  };

  const getPaymentMethodName = (methodId) => {
    const method = PAYMENT_METHODS.find(m => m.id === methodId);
    return method ? method.name : methodId;
  };

  return (
    <div className="space-y-6" data-testid="expenses-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#d4c4b0]">Travel & Expenses</h1>
          <p className="text-[#6b7280]">Track business expenses and reimbursements</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openNewTrip} className="border-border text-white" data-testid="new-trip-btn">
            <FolderOpen className="w-4 h-4 mr-2" />
            New Trip/Project
          </Button>
          <Button onClick={openNewExpense} className="bg-primary hover:bg-primary/90" data-testid="new-expense-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Expense
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Receipt className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-[#6b7280]">Total Expenses</p>
              <p className="text-xl font-bold text-[#d4c4b0]">${(stats.total_amount || 0).toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-yellow-500/10 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-500" />
            </div>
            <div>
              <p className="text-sm text-[#6b7280]">Pending Approval</p>
              <p className="text-xl font-bold text-[#d4c4b0]">{stats.by_status?.submitted || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-500/10 rounded-lg">
              <DollarSign className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-[#6b7280]">Pending Reimbursement</p>
              <p className="text-xl font-bold text-[#d4c4b0]">${(stats.pending_reimbursement || 0).toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-500/10 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-[#6b7280]">Reimbursed</p>
              <p className="text-xl font-bold text-[#d4c4b0]" data-testid="reimbursed-amount-stat">${(stats.reimbursed_amount || 0).toLocaleString()}</p>
              <p className="text-xs text-[#6b7280]">{stats.by_status?.reimbursed || 0} expense{(stats.by_status?.reimbursed || 0) !== 1 ? 's' : ''}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-secondary border border-border">
          <TabsTrigger value="expenses" className="data-[state=active]:bg-primary">Expenses</TabsTrigger>
          <TabsTrigger value="trips" className="data-[state=active]:bg-primary">Trips/Projects</TabsTrigger>
        </TabsList>

        {/* Expenses Tab */}
        <TabsContent value="expenses">
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-[#d4c4b0]">Expense List</CardTitle>
                <div className="flex gap-2">
                  <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-[150px] bg-secondary border-border text-white">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="all" className="text-white">All Status</SelectItem>
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key} className="text-white">{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filterCategory || "all"} onValueChange={(v) => setFilterCategory(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-[180px] bg-secondary border-border text-white">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="all" className="text-white">All Categories</SelectItem>
                      {EXPENSE_CATEGORIES.map(cat => (
                        <SelectItem key={cat.id} value={cat.id} className="text-white">{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filterTrip || "all"} onValueChange={(v) => setFilterTrip(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-[180px] bg-secondary border-border text-white">
                      <SelectValue placeholder="All Trips" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="all" className="text-white">All Trips</SelectItem>
                      {trips.map(trip => (
                        <SelectItem key={trip.trip_id} value={trip.trip_id} className="text-white">{trip.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-48">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : expenses.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-[#6b7280]">
                  <Receipt className="w-12 h-12 mb-3 opacity-50" />
                  <p>No expenses found</p>
                  <Button variant="link" onClick={openNewExpense} className="text-primary mt-2">
                    Add your first expense
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border hover:bg-transparent">
                        <TableHead className="text-[#8b9299]">Date</TableHead>
                        <TableHead className="text-[#8b9299]">Category</TableHead>
                        <TableHead className="text-[#8b9299]">Description</TableHead>
                        <TableHead className="text-[#8b9299]">Trip</TableHead>
                        <TableHead className="text-[#8b9299]">Payment</TableHead>
                        <TableHead className="text-[#8b9299]">Status</TableHead>
                        <TableHead className="text-[#8b9299] text-right">Amount</TableHead>
                        <TableHead className="text-[#8b9299] text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {expenses.map((expense) => {
                        const config = STATUS_CONFIG[expense.status] || STATUS_CONFIG.draft;
                        const CategoryIcon = getCategoryIcon(expense.category);
                        return (
                          <TableRow key={expense.expense_id} className="border-border hover:bg-secondary/50" data-testid={`expense-row-${expense.expense_id}`}>
                            <TableCell className="text-[#d4c4b0]">{expense.date}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2 text-[#d4c4b0]">
                                <CategoryIcon className="w-4 h-4 text-[#8b9299]" />
                                <span className="text-sm">{getCategoryName(expense.category)}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-[#8b9299] max-w-[200px]">
                              <div className="flex items-center gap-2">
                                <div className="truncate">
                                  {expense.merchant && <span className="text-[#d4c4b0]">{expense.merchant}</span>}
                                  {expense.merchant && expense.description && " - "}
                                  {expense.description}
                                  {expense.mileage && <span className="text-[#6b7280] ml-1">({expense.mileage} mi)</span>}
                                </div>
                                {expense.receipt_url && (
                                  <Paperclip className="w-3 h-3 text-green-400 flex-shrink-0" title="Receipt attached" />
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-[#8b9299]">{expense.trip_name || "-"}</TableCell>
                            <TableCell className="text-[#8b9299] text-sm">
                              {expense.payment_method === "company_card" ? (
                                <span className="text-blue-400">Company</span>
                              ) : (
                                <span className="text-amber-400">Personal</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge className={config.color}>{config.label}</Badge>
                            </TableCell>
                            <TableCell className="text-[#d4c4b0] text-right font-mono font-medium">
                              ${expense.amount.toFixed(2)}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                {/* Receipt actions - always visible */}
                                {expense.receipt_url ? (
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-green-400 hover:text-green-300" onClick={() => viewReceipt(expense)} title="View Receipt">
                                    <Image className="w-4 h-4" />
                                  </Button>
                                ) : (
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-[#6b7280] hover:text-[#8b9299]" onClick={() => openReceiptUpload(expense)} title="Upload Receipt">
                                    <Paperclip className="w-4 h-4" />
                                  </Button>
                                )}
                                {/* Draft actions */}
                                {expense.status === "draft" && (
                                  <>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-[#8b9299] hover:text-white" onClick={() => openEditExpense(expense)} title="Edit">
                                      <Edit2 className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-400 hover:text-blue-300" onClick={() => handleSubmitExpense(expense)} title="Submit for Approval">
                                      <Send className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => handleDeleteExpense(expense)} title="Delete">
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                                {/* Submitted - approval actions */}
                                {expense.status === "submitted" && (
                                  <>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-green-400 hover:text-green-300" onClick={() => handleApproveExpense(expense)} title="Approve">
                                      <CheckCircle className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-300" onClick={() => openRejectDialog(expense)} title="Reject">
                                      <XCircle className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                                {/* Approved - reimburse action */}
                                {expense.status === "approved" && expense.payment_method !== "company_card" && (
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-green-400 hover:text-green-300" onClick={() => handleReimburse(expense)} title="Mark as Reimbursed">
                                    <DollarSign className="w-4 h-4" />
                                  </Button>
                                )}
                                {/* Rejected - can edit and resubmit */}
                                {expense.status === "rejected" && (
                                  <>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-[#8b9299] hover:text-white" onClick={() => openEditExpense(expense)} title="Edit & Resubmit">
                                      <Edit2 className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => handleDeleteExpense(expense)} title="Delete">
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trips Tab */}
        <TabsContent value="trips">
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-[#d4c4b0]">Trips & Projects</CardTitle>
            </CardHeader>
            <CardContent>
              {trips.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-[#6b7280]">
                  <FolderOpen className="w-12 h-12 mb-3 opacity-50" />
                  <p>No trips or projects</p>
                  <Button variant="link" onClick={openNewTrip} className="text-primary mt-2">
                    Create your first trip
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {trips.map((trip) => (
                    <Card key={trip.trip_id} className="bg-secondary border-border" data-testid={`trip-card-${trip.trip_id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-medium text-[#d4c4b0]">{trip.name}</h3>
                            {trip.destination && (
                              <p className="text-sm text-[#8b9299] flex items-center gap-1">
                                <MapPin className="w-3 h-3" /> {trip.destination}
                              </p>
                            )}
                          </div>
                          <Badge className={trip.status === "active" ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-400"}>
                            {trip.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-[#6b7280] mb-3 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {trip.start_date} {trip.end_date && `- ${trip.end_date}`}
                        </p>
                        {trip.purpose && (
                          <p className="text-sm text-[#8b9299] mb-3 line-clamp-2">{trip.purpose}</p>
                        )}
                        <Separator className="bg-border/50 my-3" />
                        {(trip.draft_count > 0 || trip.pending_count > 0 || trip.approved_count > 0) && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {trip.draft_count > 0 && (
                              <Button size="sm" variant="outline" className="h-7 text-xs border-blue-500/40 text-blue-400 hover:text-blue-300"
                                onClick={() => handleTripBulkAction(trip, 'submit')} data-testid={`trip-bulk-submit-${trip.trip_id}`}>
                                <Send className="w-3 h-3 mr-1" /> Submit All ({trip.draft_count})
                              </Button>
                            )}
                            {trip.pending_count > 0 && (
                              <Button size="sm" variant="outline" className="h-7 text-xs border-green-500/40 text-green-400 hover:text-green-300"
                                onClick={() => handleTripBulkAction(trip, 'approve')} data-testid={`trip-bulk-approve-${trip.trip_id}`}>
                                <CheckCircle className="w-3 h-3 mr-1" /> Approve All ({trip.pending_count})
                              </Button>
                            )}
                            {trip.approved_count > 0 && (
                              <Button size="sm" variant="outline" className="h-7 text-xs border-amber-500/40 text-amber-400 hover:text-amber-300"
                                onClick={() => handleTripBulkAction(trip, 'reimburse')} data-testid={`trip-bulk-reimburse-${trip.trip_id}`}>
                                <DollarSign className="w-3 h-3 mr-1" /> Reimburse All ({trip.approved_count})
                              </Button>
                            )}
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <div className="text-sm">
                            <span className="text-[#6b7280]">{trip.expense_count || 0} expenses</span>
                            <span className="mx-2 text-[#6b7280]">•</span>
                            <span className="text-[#d4c4b0] font-medium">${(trip.total_expenses || 0).toLocaleString()}</span>
                          </div>
                          <div className="flex gap-1">
                            {trip.expense_count > 0 && (
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-primary hover:text-primary/80" onClick={() => handleTripReport(trip)} title="Download Expense Report PDF" data-testid={`trip-report-${trip.trip_id}`}>
                                <FileText className="w-4 h-4" />
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-[#8b9299] hover:text-white" onClick={() => openEditTrip(trip)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => handleDeleteTrip(trip)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Expense Dialog */}
      <Dialog open={showExpenseDialog} onOpenChange={setShowExpenseDialog}>
        <DialogContent className="bg-card border-border max-w-lg" aria-describedby={undefined} data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#d4c4b0]">
              {editingExpense ? "Edit Expense" : "New Expense"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#8b9299] text-sm">Date</Label>
                <Input 
                  type="date" 
                  value={expenseDate} 
                  onChange={(e) => setExpenseDate(e.target.value)} 
                  className="mt-1.5 bg-secondary border-border text-white"
                  data-testid="expense-date"
                />
              </div>
              <div>
                <Label className="text-[#8b9299] text-sm">Category</Label>
                <Select value={expenseCategory} onValueChange={setExpenseCategory}>
                  <SelectTrigger className="mt-1.5 bg-secondary border-border text-white" data-testid="expense-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {EXPENSE_CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id} className="text-white">{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {expenseCategory === "vehicle_mileage" ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#8b9299] text-sm">Miles Driven</Label>
                  <Input 
                    type="number" 
                    step="0.1" 
                    value={expenseMileage} 
                    onChange={(e) => setExpenseMileage(e.target.value)} 
                    placeholder="e.g., 125.5"
                    className="mt-1.5 bg-secondary border-border text-white"
                    data-testid="expense-mileage"
                  />
                </div>
                <div>
                  <Label className="text-[#8b9299] text-sm">Amount (auto-calculated)</Label>
                  <Input 
                    type="text" 
                    value={expenseMileage ? `$${(parseFloat(expenseMileage) * IRS_MILEAGE_RATE).toFixed(2)}` : "$0.00"}
                    disabled
                    className="mt-1.5 bg-secondary/50 border-border text-[#8b9299]"
                  />
                  <p className="text-xs text-[#6b7280] mt-1">@ ${IRS_MILEAGE_RATE}/mile (IRS rate)</p>
                </div>
              </div>
            ) : (
              <div>
                <Label className="text-[#8b9299] text-sm">Amount</Label>
                <Input 
                  type="number" 
                  step="0.01" 
                  value={expenseAmount} 
                  onChange={(e) => setExpenseAmount(e.target.value)} 
                  placeholder="0.00"
                  className="mt-1.5 bg-secondary border-border text-white"
                  data-testid="expense-amount"
                />
              </div>
            )}

            <div>
              <Label className="text-[#8b9299] text-sm">Merchant / Vendor</Label>
              <Input 
                value={expenseMerchant} 
                onChange={(e) => setExpenseMerchant(e.target.value)} 
                placeholder="e.g., Delta Airlines, Marriott"
                className="mt-1.5 bg-secondary border-border text-white"
                data-testid="expense-merchant"
              />
            </div>

            <div>
              <Label className="text-[#8b9299] text-sm">Description</Label>
              <Textarea 
                value={expenseDescription} 
                onChange={(e) => setExpenseDescription(e.target.value)} 
                placeholder="Brief description of the expense..."
                className="mt-1.5 bg-secondary border-border text-white"
                rows={2}
                data-testid="expense-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#8b9299] text-sm">Payment Method</Label>
                <Select value={expensePaymentMethod} onValueChange={setExpensePaymentMethod}>
                  <SelectTrigger className="mt-1.5 bg-secondary border-border text-white" data-testid="expense-payment">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {PAYMENT_METHODS.map(method => (
                      <SelectItem key={method.id} value={method.id} className="text-white">{method.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#8b9299] text-sm">Trip/Project (Optional)</Label>
                <Select value={expenseTripId || "none"} onValueChange={(v) => setExpenseTripId(v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1.5 bg-secondary border-border text-white" data-testid="expense-trip">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="none" className="text-white">None</SelectItem>
                    {trips.map(trip => (
                      <SelectItem key={trip.trip_id} value={trip.trip_id} className="text-white">{trip.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-[#8b9299] text-sm">Receipt (Optional)</Label>
              <label className="mt-1.5 flex items-center gap-3 p-3 border-2 border-dashed border-border rounded-lg cursor-pointer bg-secondary/30 hover:bg-secondary/50 transition-colors" data-testid="expense-receipt-dropzone">
                <input
                  type="file"
                  className="hidden"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  onChange={handleDialogReceiptSelect}
                  data-testid="expense-receipt-input"
                />
                <Paperclip className="w-4 h-4 text-[#8b9299] flex-shrink-0" />
                {expenseReceiptFile ? (
                  <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                    <span className="text-sm text-[#d4c4b0] truncate" data-testid="expense-receipt-filename">{expenseReceiptFile.name}</span>
                    <button
                      type="button"
                      className="text-xs text-red-400 hover:text-red-300 flex-shrink-0"
                      onClick={(e) => { e.preventDefault(); setExpenseReceiptFile(null); }}
                      data-testid="expense-receipt-remove"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <span className="text-sm text-[#8b9299]">
                    {editingExpense?.receipt_url ? "Receipt attached — choose a file to replace it" : "Attach receipt — JPEG, PNG, WebP or PDF (max 10MB)"}
                  </span>
                )}
              </label>
            </div>

            {expensePaymentMethod !== "company_card" && (
              <p className="text-sm text-amber-400 bg-amber-500/10 p-2 rounded">
                This expense will require reimbursement after approval.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExpenseDialog(false)} className="border-border text-white">
              Cancel
            </Button>
            <Button onClick={handleSaveExpense} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-expense-btn">
              {saving ? "Saving..." : editingExpense ? "Update Expense" : "Create Expense"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trip Dialog */}
      <Dialog open={showTripDialog} onOpenChange={setShowTripDialog}>
        <DialogContent className="bg-card border-border max-w-lg" aria-describedby={undefined} data-testid="trip-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#d4c4b0]">
              {editingTrip ? "Edit Trip/Project" : "New Trip/Project"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-[#8b9299] text-sm">Trip/Project Name *</Label>
              <Input 
                value={tripName} 
                onChange={(e) => setTripName(e.target.value)} 
                placeholder="e.g., Q1 Sales Conference, Client Site Visit"
                className="mt-1.5 bg-secondary border-border text-white"
                data-testid="trip-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#8b9299] text-sm">Start Date</Label>
                <Input 
                  type="date" 
                  value={tripStartDate} 
                  onChange={(e) => setTripStartDate(e.target.value)} 
                  className="mt-1.5 bg-secondary border-border text-white"
                  data-testid="trip-start-date"
                />
              </div>
              <div>
                <Label className="text-[#8b9299] text-sm">End Date</Label>
                <Input 
                  type="date" 
                  value={tripEndDate} 
                  onChange={(e) => setTripEndDate(e.target.value)} 
                  className="mt-1.5 bg-secondary border-border text-white"
                  data-testid="trip-end-date"
                />
              </div>
            </div>
            <div>
              <Label className="text-[#8b9299] text-sm">Destination</Label>
              <Input 
                value={tripDestination} 
                onChange={(e) => setTripDestination(e.target.value)} 
                placeholder="e.g., New York, NY"
                className="mt-1.5 bg-secondary border-border text-white"
                data-testid="trip-destination"
              />
            </div>
            <div>
              <Label className="text-[#8b9299] text-sm">Purpose / Description</Label>
              <Textarea 
                value={tripPurpose} 
                onChange={(e) => setTripPurpose(e.target.value)} 
                placeholder="Business purpose of this trip..."
                className="mt-1.5 bg-secondary border-border text-white"
                rows={3}
                data-testid="trip-purpose"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTripDialog(false)} className="border-border text-white">
              Cancel
            </Button>
            <Button onClick={handleSaveTrip} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-trip-btn">
              {saving ? "Saving..." : editingTrip ? "Update Trip" : "Create Trip"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#d4c4b0]">Reject Expense</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-[#8b9299] text-sm">Reason for Rejection *</Label>
            <Textarea 
              value={rejectionReason} 
              onChange={(e) => setRejectionReason(e.target.value)} 
              placeholder="Please provide a reason for rejecting this expense..."
              className="mt-1.5 bg-secondary border-border text-white"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)} className="border-border text-white">
              Cancel
            </Button>
            <Button onClick={handleRejectExpense} className="bg-destructive hover:bg-destructive/90">
              Reject Expense
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Upload Dialog */}
      <Dialog open={showReceiptDialog} onOpenChange={setShowReceiptDialog}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#d4c4b0]">Upload Receipt</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {uploadingExpense && (
              <div className="mb-4 p-3 bg-secondary/50 rounded-lg">
                <p className="text-sm text-[#8b9299]">Expense:</p>
                <p className="text-[#d4c4b0] font-medium">
                  {getCategoryName(uploadingExpense.category)} - ${uploadingExpense.amount.toFixed(2)}
                </p>
                <p className="text-sm text-[#6b7280]">{uploadingExpense.date}</p>
              </div>
            )}
            <Label className="text-[#8b9299] text-sm">Select Receipt File</Label>
            <div className="mt-2">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-lg cursor-pointer bg-secondary/30 hover:bg-secondary/50 transition-colors">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  {uploadingReceipt ? (
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-[#8b9299] mb-2" />
                      <p className="text-sm text-[#8b9299]">
                        <span className="font-medium text-primary">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-[#6b7280] mt-1">JPEG, PNG, WebP, or PDF (max 10MB)</p>
                    </>
                  )}
                </div>
                <input 
                  type="file" 
                  className="hidden" 
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  onChange={handleReceiptUpload}
                  disabled={uploadingReceipt}
                />
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReceiptDialog(false)} className="border-border text-white" disabled={uploadingReceipt}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Preview Dialog */}
      <Dialog open={showReceiptPreview} onOpenChange={setShowReceiptPreview}>
        <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-[#d4c4b0] flex items-center justify-between">
              <span>Receipt Preview</span>
              {previewReceipt && (
                <Button variant="ghost" size="sm" asChild className="text-primary">
                  <a href={previewReceipt.url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open in New Tab
                  </a>
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {previewReceipt && (
              <>
                <div className="mb-4 p-3 bg-secondary/50 rounded-lg flex items-center justify-between">
                  <div>
                    <p className="text-[#d4c4b0] font-medium">
                      {getCategoryName(previewReceipt.expense.category)} - ${previewReceipt.expense.amount.toFixed(2)}
                    </p>
                    <p className="text-sm text-[#6b7280]">
                      {previewReceipt.expense.merchant && `${previewReceipt.expense.merchant} • `}
                      {previewReceipt.expense.date}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => openReceiptUpload(previewReceipt.expense)} className="border-border text-white">
                    <Upload className="w-4 h-4 mr-2" />
                    Replace
                  </Button>
                </div>
                <div className="bg-secondary/30 rounded-lg overflow-hidden flex items-center justify-center min-h-[400px]">
                  {previewReceipt.url.endsWith('.pdf') ? (
                    <div className="text-center p-8">
                      <FileText className="w-16 h-16 text-[#8b9299] mx-auto mb-4" />
                      <p className="text-[#8b9299] mb-4">PDF Receipt</p>
                      <Button asChild className="bg-primary hover:bg-primary/90">
                        <a href={previewReceipt.url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4 mr-2" />
                          View PDF
                        </a>
                      </Button>
                    </div>
                  ) : (
                    <img 
                      src={previewReceipt.url} 
                      alt="Receipt" 
                      className="max-w-full max-h-[60vh] object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  )}
                  <div className="hidden text-center p-8">
                    <Image className="w-16 h-16 text-[#8b9299] mx-auto mb-4" />
                    <p className="text-[#8b9299]">Unable to load image</p>
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReceiptPreview(false)} className="border-border text-white">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
